import { useEffect, useMemo, useRef } from "react";
import { useAppDispatch, useAppSelector } from "../redux/hooks";
import { useUpdateUserStateItemMutation } from "../redux/services/userStateApi";
import type { UserStateEntry, UserStateItemKey } from "../types/user-state";
import { setEntry } from "../redux/features/userState/userStateSlice";
import { stableHash } from '../utils/text/stable-hash';

type ConflictAwareError = {
  status?: number;
  data?: unknown;
  error?: {
    status?: number;
    data?: unknown;
  };
};

const SYNC_DELAY_MS = 750;

function extractConflictEntry(error: unknown): UserStateEntry | null {
  if (!error || typeof error !== "object") {
    return null;
  }
  const conflict = error as ConflictAwareError;
  if (conflict.status === 409 && conflict.data && typeof conflict.data === "object") {
    return conflict.data as UserStateEntry;
  }
  if (
    conflict.error &&
    conflict.error.status === 409 &&
    conflict.error.data &&
    typeof conflict.error.data === "object"
  ) {
    return conflict.error.data as UserStateEntry;
  }
  return null;
}

function normalizePayload<T extends Record<string, unknown>>(payload: T): Record<string, unknown> {
  return JSON.parse(JSON.stringify(payload));
}

function useSyncUserStateItem(
  itemKey: UserStateItemKey,
  payload: Record<string, unknown>,
  userId?: string | null
) {
  const dispatch = useAppDispatch();
  const entry = useAppSelector((state) => state.userState.entries[itemKey]);
  const [updateUserStateItem] = useUpdateUserStateItemMutation();
  const lastSyncedHashRef = useRef<string | null>(null);
  const normalizedPayload = useMemo(() => normalizePayload(payload), [payload]);
  const payloadHash = useMemo(() => stableHash(normalizedPayload), [normalizedPayload]);
  const entryHash = useMemo(
    () => (entry && entry.data ? stableHash(entry.data) : null),
    [entry]
  );

  useEffect(() => {
    if (entryHash) {
      lastSyncedHashRef.current = entryHash;
    }
  }, [entryHash]);

  useEffect(() => {
    if (!userId) {
      lastSyncedHashRef.current = null;
    }
  }, [userId]);

  useEffect(() => {
    if (!userId) {
      return;
    }
    if (typeof window === "undefined") {
      return;
    }

    if (payloadHash === entryHash) {
      lastSyncedHashRef.current = payloadHash;
      return;
    }

    if (payloadHash === lastSyncedHashRef.current) {
      return;
    }

    lastSyncedHashRef.current = payloadHash;

    const version = entry?.version ?? 0;

    let cancelled = false;
    const timeout = window.setTimeout(async () => {
      if (cancelled) return;
      try {
        const result = await updateUserStateItem({
          itemKey,
          data: normalizedPayload,
          version,
        }).unwrap();
        if (!cancelled && result) {
          dispatch(setEntry({ itemKey, entry: result }));
          lastSyncedHashRef.current = result?.data ? stableHash(result.data) : payloadHash;
        }
      } catch (error) {
        const conflict = extractConflictEntry(error);
        if (conflict) {
          console.info(`[user-state] Conflict syncing ${itemKey}, adopting server version`);
          dispatch(setEntry({ itemKey, entry: conflict }));
          lastSyncedHashRef.current = conflict.data ? stableHash(conflict.data) : null;
        } else {
          lastSyncedHashRef.current = null;
          console.error(`[user-state] Failed to sync ${itemKey}`, error);
        }
      }
    }, SYNC_DELAY_MS);

    return () => {
      cancelled = true;
      window.clearTimeout(timeout);
    };
  }, [
    dispatch,
    entry?.version,
    entryHash,
    itemKey,
    normalizedPayload,
    payloadHash,
    updateUserStateItem,
    userId,
  ]);
}

export function useProfilePreferencesSettingsSync(userId?: string | null): void {
  const jobForm = useAppSelector((state) => state.jobForm);
  const comparisonPreferences = useAppSelector((state) => state.comparisonPreferences);
  const lang = useAppSelector((state) => state.lang.value);
  const ui = useAppSelector((state) => state.ui);

  const jobFormPayload = useMemo(() => normalizePayload(jobForm), [jobForm]);
  const comparisonPreferencesPayload = useMemo(
    () => normalizePayload(comparisonPreferences),
    [comparisonPreferences]
  );
  const settingsPayload = useMemo(() => normalizePayload({ lang, ui }), [lang, ui]);

  useSyncUserStateItem("profile", jobFormPayload, userId);
  useSyncUserStateItem("preferences", comparisonPreferencesPayload, userId);
  useSyncUserStateItem("settings", settingsPayload, userId);
}

